import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { ROLES } from "../../constants/roles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import MasterPageBreadcrumb from "../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../layout/master/masterRouteMeta";
import { MASTER_HOME } from "../../helpers/masterUser";
import { formatBytes, formatPercent, formatUptime } from "../../helpers/formatBytes";
import {
	buildSystemMetricSummary,
	metricDetailLines,
} from "../../helpers/systemMetrics";
import MasterStatCard from "../../components/Master/MasterStatCard";
import MasterSpinner from "../../components/Master/MasterSpinner";
import MasterProgressMetric from "../../components/Master/MasterProgressMetric";
import MasterDonutChart from "../../components/Master/MasterDonutChart";

const AUTO_RELOAD_MS = 30000;

const IconCpu = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<rect x="4" y="4" width="16" height="16" rx="2" />
		<rect x="9" y="9" width="6" height="6" />
		<path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
	</svg>
);
const IconRam = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<path d="M6 19v-3M10 19v-3M14 19v-3M18 19v-3M4 11h16v8H4zM7 11V7h10v4" />
	</svg>
);
const IconDisk = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<ellipse cx="12" cy="5" rx="9" ry="3" />
		<path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
		<path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
	</svg>
);
const IconClock = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<circle cx="12" cy="12" r="10" />
		<polyline points="12 6 12 12 16 14" />
	</svg>
);

export default function SystemAnalysis() {
	const history = useHistory();
	const { user, loading: authLoading } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const fetchSystemInfo = useCallback((silent = false) => {
		setIsRefreshing(true);
		if (!silent) {
			setError(null);
			setLoading(true);
		}
		api
			.get("/master/system-info")
			.then(({ data: res }) => setData(res))
			.catch((err) => {
				if (!silent) {
					toastError(err);
					setError(err.response?.data?.error || i18n.t("master.system.loadError"));
				}
			})
			.finally(() => {
				setIsRefreshing(false);
				if (!silent) setLoading(false);
			});
	}, []);

	const fetchRef = useRef(fetchSystemInfo);
	fetchRef.current = fetchSystemInfo;

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/admin-master");
			return;
		}
		fetchSystemInfo(false);
	}, [user, authLoading, history, fetchSystemInfo]);

	useEffect(() => {
		if (!user || user.profile !== ROLES.master) return;
		const interval = setInterval(() => fetchRef.current(true), AUTO_RELOAD_MS);
		return () => clearInterval(interval);
	}, [user?.profile]);

	const summary = useMemo(() => buildSystemMetricSummary(data), [data]);
	const details = useMemo(() => metricDetailLines(summary, i18n), [summary]);
	const trackColor = darkMode ? "#3f3f46" : "#e4e4e7";

	if (authLoading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const routeMeta = getMasterRouteMeta(`${MASTER_HOME}/system`);

	const headerSubtitle = (
		<>
			{i18n.t("master.pages.systemSubtitle")}
			{data?.updatedAt && (
				<>
					{" · "}
					<strong>{i18n.t("master.pages.systemLastUpdate")}:</strong>{" "}
					{new Date(data.updatedAt).toLocaleTimeString("pt-BR", {
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					})}
				</>
			)}
			{isRefreshing && (
				<span className="ml-2 inline-flex align-middle" aria-label={i18n.t("master.system.refreshing")}>
					<MasterSpinner size={14} />
				</span>
			)}
		</>
	);

	return (
		<div className="mx-auto w-full max-w-7xl box-border">
			<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<MasterPageBreadcrumb
						title={i18n.t(routeMeta.titleKey)}
						breadcrumbs={routeMeta.breadcrumbs}
					/>
					{routeMeta.subtitleKey ? (
						<p className="-mt-2 text-sm text-default-500 dark:text-default-400">
							{i18n.t(routeMeta.subtitleKey)}
						</p>
					) : null}
					<p className="mt-1 text-sm text-default-500 dark:text-default-400">{headerSubtitle}</p>
					{details.host && (
						<p className="mt-1 text-xs text-default-400">{details.host}</p>
					)}
				</div>
				<button
					type="button"
					className="btn btn-primary"
					onClick={() => fetchSystemInfo(false)}
					disabled={isRefreshing}
				>
					{i18n.t("master.pages.systemRefresh")}
				</button>
			</div>

			{loading && !data && (
				<div className="master-loading py-16">
					<MasterSpinner />
					<span className="mt-3 text-sm text-default-500">{i18n.t("master.system.loading")}</span>
				</div>
			)}

			{error && !data && (
				<div className="card">
					<div className="card-body py-12 text-center">
						<p className="m-0 font-semibold text-danger">{error}</p>
						<button type="button" className="btn btn-primary mt-4" onClick={() => fetchSystemInfo(false)}>
							{i18n.t("master.system.retry")}
						</button>
					</div>
				</div>
			)}

			{summary && (
				<>
					<div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
						<MasterStatCard
							tone="primary"
							icon={<IconCpu />}
							value={formatPercent(summary.cpu)}
							label={i18n.t("master.system.cpu")}
						/>
						<MasterStatCard
							tone="success"
							icon={<IconRam />}
							value={formatPercent(summary.ram)}
							label={i18n.t("master.system.ram")}
						/>
						<MasterStatCard
							tone="secondary"
							icon={<IconDisk />}
							value={formatPercent(summary.disk)}
							label={i18n.t("master.system.storage")}
						/>
						<MasterStatCard
							tone="info"
							icon={<IconClock />}
							value={summary.uptime != null ? formatUptime(summary.uptime) : "—"}
							label={i18n.t("master.system.uptime")}
						/>
					</div>

					<div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
						<div className="card">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.system.cpu")}</h3>
								{data.cpu?.cores > 0 && (
									<span className="text-xs text-default-500">
										{data.cpu.cores} {i18n.t("master.system.cores")}
									</span>
								)}
							</div>
							<div className="card-body">
								<MasterDonutChart percent={summary.cpu} color="#3b82f6" trackColor={trackColor} />
								<div className="mt-4">
									<MasterProgressMetric
										label={i18n.t("master.system.usage")}
										percent={summary.cpu}
										barClass="bg-primary"
									/>
								</div>
							</div>
						</div>

						<div className="card">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.system.ram")}</h3>
							</div>
							<div className="card-body">
								<MasterDonutChart percent={summary.ram} color="#22c55e" trackColor={trackColor} />
								<div className="mt-4">
									<MasterProgressMetric
										label={i18n.t("master.system.usage")}
										percent={summary.ram}
										detail={details.ram}
										barClass="bg-success"
									/>
								</div>
							</div>
						</div>

						<div className="card">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.system.storage")}</h3>
							</div>
							<div className="card-body">
								<MasterDonutChart percent={summary.disk} color="#f59e0b" trackColor={trackColor} />
								<div className="mt-4">
									<MasterProgressMetric
										label={i18n.t("master.system.usage")}
										percent={summary.disk}
										detail={details.disk}
										barClass="bg-warning"
									/>
								</div>
							</div>
						</div>
					</div>

					{data.disks?.length > 0 && (
						<div className="card">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.system.volumes")}</h3>
								<span className="text-xs text-default-500">
									{data.disks.length} {i18n.t("master.system.volumeCount")}
								</span>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead>
										<tr className="border-b border-default-200 text-left text-xs uppercase text-default-500 dark:border-default-700">
											<th className="px-4 py-3">{i18n.t("master.system.mount")}</th>
											<th className="px-4 py-3">{i18n.t("master.system.fs")}</th>
											<th className="px-4 py-3">{i18n.t("master.system.usage")}</th>
											<th className="px-4 py-3">{i18n.t("master.system.capacity")}</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-default-200 dark:divide-default-700">
										{data.disks.map((d, i) => (
											<tr key={`${d.mount}-${i}`} className="text-default-800 dark:text-default-200">
												<td className="px-4 py-3 font-medium">{d.mount || "—"}</td>
												<td className="px-4 py-3 text-default-500">{d.fs || "—"}</td>
												<td className="px-4 py-3">
													<div className="flex min-w-[140px] flex-col gap-1">
														<span className="text-xs font-medium">{d.usePercent ?? 0}%</span>
														<div className="h-2 w-full rounded-full bg-default-200 dark:bg-default-700">
															<div
																className="h-2 rounded-full bg-warning"
																style={{ width: `${Math.min(100, d.usePercent ?? 0)}%` }}
															/>
														</div>
													</div>
												</td>
												<td className="px-4 py-3 text-default-500">
													{formatBytes(d.used)} / {formatBytes(d.size)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}
